import { useCallback, useEffect, useRef, useState } from 'react'
import { type Blocker, type Location, useBlocker } from 'react-router-dom'
import { observer } from 'mobx-react'

import { OrderRoutePathsEnum } from 'Modules/order/routes/orderRouteConfig.types'
import { RentalsRoutePathsEnum } from 'Modules/rentals/routes/rentalsRouteConfig.types'

import { PreviewColumnTypeEnum } from 'Components/PreviewColumn/PreviewColumn.types'

import { useStore } from 'Stores/index'

import { useModal } from 'Hooks'

import { OrderGuardModalContent } from './OrderGuardModalContent'

export const OrderGuardModal = observer(() => {
  const [isNavigationAllowed, setIsNavigationAllowed] = useState(false)
  const { orderStore, portalSettingsStore } = useStore()
  const { setChosenDevice, setFetchError, setMutationError, mutationError } =
    orderStore
  const { setTogglePreviewColumn, previewColumnSettings } = portalSettingsStore

  const { setModal, hideModal } = useModal()

  const blockerRef = useRef<Blocker | null>(null)

  const handleLeaveOrder = useCallback(() => {
    hideModal()
    setIsNavigationAllowed(true)
    setChosenDevice(null)
    setFetchError(false)
    setMutationError(false)

    const existingPreviewColumnBundleSetting = previewColumnSettings.find(
      previewColumnSetting =>
        previewColumnSetting.type === PreviewColumnTypeEnum.Bundle,
    )

    if (
      existingPreviewColumnBundleSetting &&
      existingPreviewColumnBundleSetting.isOpen
    ) {
      setTogglePreviewColumn(PreviewColumnTypeEnum.Bundle, false)
    }
  }, [
    hideModal,
    previewColumnSettings,
    setChosenDevice,
    setFetchError,
    setMutationError,
    setTogglePreviewColumn,
  ])

  const handleOpenGuardModal = useCallback(() => {
    setModal({
      component: OrderGuardModalContent,
      contentClassName: 'OrderGuardModal-content',
      isOpen: true,
      props: {
        onContinue: () => {
          hideModal()

          if (
            blockerRef.current &&
            typeof blockerRef.current.reset === 'function'
          ) {
            blockerRef.current.reset()
          }
        },
        onLeave: () => {
          handleLeaveOrder()
          hideModal()

          if (
            blockerRef.current &&
            typeof blockerRef.current.proceed === 'function'
          ) {
            blockerRef.current.proceed()
          }
        },
      },
    })
  }, [blockerRef, handleLeaveOrder, hideModal, setModal])

  blockerRef.current = useBlocker(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: Location
      nextLocation: Location
    }) => {
      // Disable GuardModal when redirecting user between prolonging and upgrade
      const disableRouteGuard =
        (currentLocation.pathname.includes('/order/prolong/') &&
          nextLocation.pathname.includes('/order/upgrade')) ||
        (currentLocation.pathname.includes('/order/upgrade') &&
          nextLocation.pathname.includes('/order/prolong'))

      const isNavigationEnabled =
        disableRouteGuard ||
        nextLocation.pathname.includes(
          OrderRoutePathsEnum.ORDER_CONFIRMATION,
        ) ||
        nextLocation.pathname.includes(
          OrderRoutePathsEnum.ORDER_CUSTOM_CONFIRMATION,
        ) ||
        nextLocation.pathname.includes(
          OrderRoutePathsEnum.ORDER_PROLONG_CONFIRMATION,
        ) ||
        nextLocation.pathname.includes(RentalsRoutePathsEnum.RENTALS) ||
        mutationError

      if (isNavigationEnabled) {
        setIsNavigationAllowed(true)

        return false
      }

      if (!isNavigationAllowed) {
        handleOpenGuardModal()

        return true
      }

      return false
    },
  )

  useEffect(() => {
    const beforeunloadHandler = (event: BeforeUnloadEvent) => {
      if (isNavigationAllowed) {
        return null
      }

      const e = event || window.event

      e.preventDefault()
      event.returnValue = ''

      return ''
    }

    window.addEventListener('beforeunload', beforeunloadHandler)

    return () => {
      window.removeEventListener('beforeunload', beforeunloadHandler)
    }
  }, [isNavigationAllowed])

  return null
})
